import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './ProviderCurrencyManagement.css'; // Add a CSS file for custom styles

// Predefined provider options
const PROVIDER_OPTIONS = [
    { value: 'smile.one', label: 'Smile.one' },
    { value: 'yokcash', label: 'Yokcash' },
    { value: 'hopestore', label: 'Hopestore' }
];

// Predefined currency options
const CURRENCY_OPTIONS = ['SOC', 'RP', 'USD'];

const ProviderCurrencyManagement = () => {
    const API_BASE = import.meta.env.VITE_API_URL;
    const [currencies, setCurrencies] = useState([]);
    const [form, setForm] = useState({ provider: '', currency: '', conversionRateToINR: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editForm, setEditForm] = useState({ provider: '', currency: '', conversionRateToINR: '' });
    const [isEditing, setIsEditing] = useState(false);
    const token = useAuth().token;

    // Fetch provider currencies on load
    useEffect(() => {
        fetchProviderCurrencies();
    }, []);

    const fetchProviderCurrencies = async () => {
        try {
            const res = await fetch(`${API_BASE}/admin/provider-currencies`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data.success) {
                setCurrencies(data.settings);
            } else {
                setError('Failed to fetch provider currencies');
            }
        } catch (err) {
            setError('Error fetching provider currencies');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE}/admin/provider-currencies`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Provider currency updated successfully');
                fetchProviderCurrencies();
                setForm({ provider: '', currency: '', conversionRateToINR: '' });
            } else {
                setError(data.message || 'Failed to update provider currency');
            }
        } catch (err) {
            setError('Error updating provider currency');
        }
    };

    const handleDelete = async (provider) => {
        if (!window.confirm(`Are you sure you want to delete settings for ${provider}?`)) return;
        try {
            const res = await fetch(`${API_BASE}/admin/provider-currencies/${provider}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Provider currency deleted successfully');
                fetchProviderCurrencies();
            } else {
                setError(data.message || 'Failed to delete provider currency');
            }
        } catch (err) {
            setError('Error deleting provider currency');
        }
    };

    const handleEditClick = (currency) => {
        setEditForm(currency);
        setIsEditing(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm({ ...editForm, [name]: value });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE}/admin/provider-currencies/${editForm.provider}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(editForm),
            });
            const data = await res.json();
            if (data.success) {
                setSuccess('Provider currency updated successfully');
                fetchProviderCurrencies();
                setIsEditing(false);
            } else {
                setError(data.message || 'Failed to update provider currency');
            }
        } catch (err) {
            setError('Error updating provider currency');
        }
    };

    return (
        <div className="provider-currency-management">
            <h2 className="title">Provider Currency Management</h2>

            {/* Success and Error Messages */}
            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* Add/Update Form */}
            <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                    <label htmlFor="provider">Provider</label>
                    <select
                        name="provider"
                        value={form.provider}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                    >
                        <option value="">Select a Provider</option>
                        {PROVIDER_OPTIONS.map((provider) => (
                            <option key={provider.value} value={provider.value}>
                                {provider.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="currency">Currency</label>
                    <select
                        name="currency"
                        value={form.currency}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                    >
                        <option value="">Select a Currency</option>
                        {CURRENCY_OPTIONS.map((currency) => (
                            <option key={currency} value={currency}>
                                {currency}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="conversionRateToINR">Conversion Rate to INR</label>
                    <input
                        type="number"
                        name="conversionRateToINR"
                        value={form.conversionRateToINR}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Conversion Rate to INR"
                        step="0.01"
                        required
                    />
                </div>
                <button type="submit" className="btn btn-primary">Save</button>
            </form>

            {/* Edit Modal */}
            {isEditing && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit Provider Currency</h3>
                        <form onSubmit={handleEditSubmit}>
                            <div className="form-group">
                                <label htmlFor="currency">Currency</label>
                                <select
                                    name="currency"
                                    value={editForm.currency}
                                    onChange={handleEditInputChange}
                                    className="form-control"
                                    required
                                >
                                    <option value="">Select a Currency</option>
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <option key={currency} value={currency}>
                                            {currency}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="conversionRateToINR">Conversion Rate to INR</label>
                                <input
                                    type="number"
                                    name="conversionRateToINR"
                                    value={editForm.conversionRateToINR}
                                    onChange={handleEditInputChange}
                                    className="form-control"
                                    placeholder="Conversion Rate to INR"
                                    step="0.01"
                                    required
                                />
                            </div>
                            <button type="submit" className="btn btn-primary">Save</button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Provider Currency List */}
            <table className="table">
                <thead>
                    <tr>
                        <th>Provider</th>
                        <th>Currency</th>
                        <th>Conversion Rate to INR</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currencies.map((currency) => (
                        <tr key={currency.provider}>
                            <td>{currency.provider}</td>
                            <td>{currency.currency}</td>
                            <td>{currency.conversionRateToINR}</td>
                            <td>
                                <button
                                    className="btn btn-warning btn-sm"
                                    onClick={() => handleEditClick(currency)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(currency.provider)}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ProviderCurrencyManagement;